import type { BrowserPhoneConfig } from '../types';
import type {
  JsSipRuntime,
  SipDirection,
  SipLogger,
  SipRegistrationSignal,
  SipTimerPort,
} from './contracts';

export type ConfiguredBrowserPhoneConfig = BrowserPhoneConfig & {
  configured: true;
  account: NonNullable<BrowserPhoneConfig['account']>;
};

export interface SipRegistrationControllerDeps {
  runtime: JsSipRuntime;
  timers: SipTimerPort;
  now: () => number;
  logger: SipLogger;
  onSignal: (signal: SipRegistrationSignal) => void;
  onSession: (session: any, direction: SipDirection) => void;
}

function eventCause(event: any): string {
  return String(
    event?.cause
    || event?.data?.cause
    || event?.message?.reason_phrase
    || '',
  ).trim();
}

function eventSession(event: any): any {
  return event?.session || event?.data?.session || null;
}

function eventOriginator(event: any): string {
  return String(event?.originator || event?.data?.originator || '')
    .toLowerCase();
}

export class SipRegistrationController {
  private readonly runtime: JsSipRuntime;
  private readonly timers: SipTimerPort;
  private readonly now: () => number;
  private readonly logger: SipLogger;
  private readonly onSignal: (signal: SipRegistrationSignal) => void;
  private readonly onSession: (session: any, direction: SipDirection) => void;
  private readonly pendingStops = new Set<any>();
  private readonly stoppedUas = new WeakSet<object>();
  private ua: any = null;
  private watchTimer: number | null = null;
  private generation = 0;
  private lastRegistrationRetryAt = 0;
  private knownConnected = false;
  private knownRegistered = false;

  constructor(deps: SipRegistrationControllerDeps) {
    this.runtime = deps.runtime;
    this.timers = deps.timers;
    this.now = deps.now;
    this.logger = deps.logger;
    this.onSignal = deps.onSignal;
    this.onSession = deps.onSession;
  }

  start(config: ConfiguredBrowserPhoneConfig): void {
    this.stop();
    const generation = this.generation;
    this.lastRegistrationRetryAt = 0;
    this.knownConnected = false;
    this.knownRegistered = false;

    const socket = new this.runtime.WebSocketInterface(config.webSocketUrl);
    socket.via_transport = 'ws';
    const proxy = String(config.outboundProxy || '').trim();
    const registrarServer = proxy
      ? (proxy.startsWith('sip:') ? proxy : `sip:${proxy}`)
      : '';
    const account = config.account;
    const ua = new this.runtime.UA({
      sockets: [socket],
      uri: account.sipUri.startsWith('sip:')
        ? account.sipUri
        : `sip:${account.sipUri}`,
      authorization_user: account.authUser,
      password: account.password,
      display_name: account.displayName,
      register: true,
      register_expires: Math.max(60, Number(config.registrationExpires || 120)),
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 30,
      no_answer_timeout: 60,
      session_timers: true,
      ...(registrarServer
        ? { registrar_server: registrarServer, use_preloaded_route: true }
        : {}),
      user_agent: 'Intez Helpdesk Browser Phone',
    });
    this.ua = ua;
    const isCurrent = () => (
      this.ua === ua && this.generation === generation
    );

    ua.on?.('connected', () => {
      if (!isCurrent()) return;
      this.knownConnected = true;
      this.onSignal({ type: 'connected' });
    });
    ua.on?.('disconnected', () => {
      if (!isCurrent()) return;
      this.knownConnected = false;
      this.knownRegistered = false;
      this.onSignal({ type: 'disconnected' });
    });
    ua.on?.('registered', () => {
      if (!isCurrent()) return;
      this.knownConnected = true;
      this.knownRegistered = true;
      this.onSignal({ type: 'registered' });
    });
    ua.on?.('unregistered', () => {
      if (!isCurrent()) return;
      this.knownConnected = this.safeBoolean(() => ua.isConnected?.());
      this.knownRegistered = false;
      this.onSignal({ type: 'unregistered' });
    });
    ua.on?.('registrationFailed', (event: any) => {
      if (!isCurrent()) return;
      this.knownRegistered = false;
      this.onSignal({
        type: 'registrationFailed',
        reason: eventCause(event) || 'АТС отклонила регистрацию',
      });
    });
    ua.on?.('newRTCSession', (event: any) => {
      if (!isCurrent()) return;
      const session = eventSession(event);
      if (!session) return;
      const direction: SipDirection = eventOriginator(event) === 'remote'
        ? 'INBOUND'
        : 'OUTBOUND';
      this.onSession(session, direction);
    });

    this.startWatch(ua, generation);
    try {
      ua.start?.();
    } catch (error) {
      this.prepareStop();
      this.finishStop();
      throw error;
    }
  }

  call(target: string, options: Record<string, unknown>): any {
    if (!this.ua) throw new Error('SIP-телефон не запущен');
    return this.ua.call?.(target, options);
  }

  isConnected(): boolean {
    return Boolean(this.ua && this.safeBoolean(() => this.ua.isConnected?.()));
  }

  isRegistered(): boolean {
    return Boolean(this.ua && this.safeBoolean(() => this.ua.isRegistered?.()));
  }

  hasUa(): boolean {
    return Boolean(this.ua);
  }

  prepareStop(): void {
    this.generation += 1;
    this.stopWatch();
    const ua = this.ua;
    this.ua = null;
    this.knownConnected = false;
    this.knownRegistered = false;
    if (ua) this.pendingStops.add(ua);
  }

  finishStop(): void {
    for (const ua of this.pendingStops) {
      this.pendingStops.delete(ua);
      if (
        (typeof ua === 'object' || typeof ua === 'function')
        && ua !== null
        && !this.stoppedUas.has(ua)
      ) {
        this.stoppedUas.add(ua);
        try {
          ua.stop?.();
        } catch (error) {
          this.logger.warn('[telephony] SIP UA stop failed', error);
        }
      }
    }
  }

  stop(): void {
    this.prepareStop();
    this.finishStop();
  }

  private startWatch(ua: any, generation: number): void {
    this.stopWatch();
    const reconcile = () => {
      if (this.ua !== ua || this.generation !== generation) return;

      const registered = this.safeBoolean(() => ua.isRegistered?.());
      const connected = this.safeBoolean(() => ua.isConnected?.());
      if (registered && !this.knownRegistered) {
        this.knownConnected = true;
        this.knownRegistered = true;
        this.onSignal({ type: 'registered' });
        return;
      }
      if (connected && !this.knownConnected) {
        this.knownConnected = true;
        this.onSignal({ type: 'connected' });
      }
      if (
        connected
        && !registered
        && this.now() - this.lastRegistrationRetryAt >= 3_000
      ) {
        this.lastRegistrationRetryAt = this.now();
        try {
          ua.register?.();
        } catch (error) {
          this.logger.warn('[telephony] registration retry failed', error);
        }
      }
    };

    reconcile();
    this.watchTimer = this.timers.setInterval(reconcile, 1_000);
  }

  private stopWatch(): void {
    if (this.watchTimer !== null) {
      this.timers.clearInterval(this.watchTimer);
      this.watchTimer = null;
    }
  }

  private safeBoolean(read: () => unknown): boolean {
    try {
      return Boolean(read());
    } catch {
      return false;
    }
  }
}
